import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { mapTopupOptions } from 'helpers/productOptionsHelpers';
import { useHistory, useLocation } from 'react-router';
import queryString from 'query-string';
import OrderTopupsView from 'views/OrderTopupsView';
import { useSelector, useDispatch } from 'react-redux';
import useMessage from 'hooks/useMessages';
import { mapSubscription } from 'helpers/subscriptionHelpers';
import { orderTopup, findSubscriptions } from 'gateways/Api';
import {
  setSubscriptionsList,
  setSubscriptionLoadFailed,
} from 'store/actions/subscription';
import PageContainer from 'containers/PageContainer';
import OrderTopupsReviewView from 'views/OrderTopupsReviewView';
import { setAppBusy } from 'store/actions/system';
import useNavHistory from 'hooks/useNavHistory';
import orderTopupsContainerPath from './paths/OrderTopupsContainerPath';

const OrderTopupsContainer = ({ getProductOptionsFunc }) => {
  const [optionsAndSolution, setOptionsAndSolution] = useState({
    options: null,
    solution: null,
  });
  const dispatch = useDispatch();

  const history = useHistory();
  const location = useLocation();
  const subscriptionsList = useSelector((state) => state.subscriptionsList);
  const [originalSubscriptionsList, setOriginalSubscriptionsList] = useState();
  const [orderingTopup, setOrderingTopup] = useState(false);
  const { planId, withSolution } = queryString.parse(location.search);
  const { genericInfoMessage, genericFailureMessage } = useMessage();
  const { navigateToClosestRoot } = useNavHistory();

  React.useEffect(() => {
    if (orderingTopup) {
      return;
    }

    if (subscriptionsList && !originalSubscriptionsList) {
      setOriginalSubscriptionsList({ ...subscriptionsList });
    }
  }, [
    subscriptionsList,
    getProductOptionsFunc,
    orderingTopup,
    originalSubscriptionsList,
  ]);
  React.useEffect(() => {
    if (!orderingTopup) {
      return;
    }
    if (
      subscriptionsList.topUps.length
      === originalSubscriptionsList.topUps.length
    ) {
      genericFailureMessage('Something went wrong with your top-up. Please try again.');
    } else {
      genericInfoMessage('All done. Your top-ups have been ordered.');
    }
    navigateToClosestRoot();
  }, [
    subscriptionsList,
    originalSubscriptionsList,
    orderingTopup,
    genericFailureMessage,
    genericInfoMessage,
    navigateToClosestRoot,
  ]);

  function onTopupSelected(option) {
    history.push(
      queryString.stringifyUrl({
        url: orderTopupsContainerPath('review'),
        query: {
          planId: option.planId,
          withSolution: option.withSolution || undefined,
        },
      }),
    );
  }

  function onTopupConfirmed(selectedOption, subscription, account, store) {
    const paymentMethod = Object.keys(account.payment_methods)
      .find((k) => account.payment_methods[k].length)
      .toUpperCase();

    dispatch(setAppBusy(true));
    // let's call the APIs to save the changes...
    orderTopup({
      subscriptionId: subscription.id,
      addressId: account.address_book.find((a) => a.is_default).id,
      paymentMethod,
      orderReason: 'Additional Lenses',
      leftQuantity: selectedOption.hasLeft ? selectedOption.quantity : null,
      rightQuantity: selectedOption.hasRight ? selectedOption.quantity : null,
      solution: withSolution,
      taxCode: store.tax_code,
    })
      .catch(() => {})
      .then(() => findSubscriptions())
      .then((subs) => dispatch(setSubscriptionsList(subs)))
      .then(() => setOrderingTopup(true))
      .catch(() => dispatch(setSubscriptionLoadFailed()))
      .finally(() => dispatch(setAppBusy(false)));
  }

  function onCancel() {
    history.goBack();
  }

  const render = (subscription, account, store) => {
    const mappedSubscription = subscription && mapSubscription(subscription);

    if (!optionsAndSolution.options) {
      getProductOptionsFunc(
        store.tax_code,
        subscription.cart.left?.lens_detail.clfit.style_number,
        subscription.cart.right?.lens_detail.clfit.style_number,
        subscription.recommendedSolution?.sku,
      )
        .then((options) => mapTopupOptions(
          options,
          subscriptionsList.toppedUpAmountWithinThreeMonths,
          subscription.recommendedSolution?.sku,
        ))
        .then((mappedResult) => setOptionsAndSolution(mappedResult));
    }

    if (!planId) {
      return (
        <OrderTopupsView
          usage={mappedSubscription.currentProduct.packSize.usage}
          maxTopup={
            mappedSubscription.currentProduct.packSize.usage === 'Monthly'
              ? 3
              : 60
          }
          plans={optionsAndSolution.options}
          onSubmit={onTopupSelected}
          onCancel={onCancel}
          solution={optionsAndSolution.solution}
        />
      );
    }

    const selectedOption = optionsAndSolution.options && optionsAndSolution.options.find(
      (p) => p.id === planId,
    );
    const items = mappedSubscription.currentProduct.items.filter(
      (i) => i.kind === 'lens',
    );
    if (withSolution) {
      items.push({
        kind: 'solution',
        title: optionsAndSolution.solution.description,
        subTitle: 'Solution',
        price: optionsAndSolution.solution.price,
        url: `https://images.specsavers.com/solutions-ie-en/${optionsAndSolution.solution.sku}.jpg`,
      });
    }

    return (
      <OrderTopupsReviewView
        selectedTopup={{
          ...selectedOption,
          items,
        }}
        onSubmit={() => onTopupConfirmed(selectedOption, subscription, account, store)}
        onCancel={onCancel}
      />
    );
  };
  return (
    <PageContainer
      logoRef="/overview"
      hasBack
      requiresAccount
      requiresLogin
      requiresSubscription
      render={render}
    />
  );
};

OrderTopupsContainer.propTypes = {
  getProductOptionsFunc: PropTypes.func.isRequired,
};

export default OrderTopupsContainer;
